import * as React from 'react';
import {
  AddPhotoSheet,
  AddTile,
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  Button,
  ConfirmDialog,
  Form,
  FormField,
  OptionSheet,
  PhotoStrip,
  Stack,
  Switch,
  Textarea,
  toast,
  useZodForm,
} from '@relay-towing/ui';
import { useCurrentPosition } from '../../hooks/useCurrentPosition';
import { useSubmitInspectionReport } from '../../hooks/useUrgently';
import { toPhotoDataUrl } from '../../lib/dataUrl';
import {
  capturePhoto,
  pickMedia,
  type PickedMedia,
} from '../../lib/nativeBridge';
import {
  URGENTLY_DOCUMENT_TYPES,
  URGENTLY_INSPECTION_TITLES,
  fitsOneRequest,
  toInspectionReport,
  urgentlyInspectionSchema,
  type UrgentlyDocumentType,
  type UrgentlyInspectionKind,
  type UrgentlyInspectionValues,
} from '../../lib/urgentlyInspectionSchema';

const EMPTY: UrgentlyInspectionValues = {
  vehicleDamaged: false,
  notes: '',
  photos: [],
};

/** A local preview of each photo, let go when the photo or sheet goes. */
function usePreviews(photos: UrgentlyInspectionValues['photos']) {
  const urls = React.useMemo(
    () => photos.map((photo) => URL.createObjectURL(photo.file)),
    [photos]
  );
  React.useEffect(
    () => () => urls.forEach((url) => URL.revokeObjectURL(url)),
    [urls]
  );
  return urls;
}

/**
 * One of Urgently's two inspections of a job's vehicle, on site or at drop
 * off: whether it is damaged, notes, and photos, each saying what it shows,
 * which Urgently files it under. Adding a photo asks what it is first, then
 * camera or library, as the old screen did. Sent as soon as it is done;
 * the server passes it on to Urgently.
 *
 * Where the phone is goes on the report and on every photo. It is asked on
 * open, which a tap opened, so a first-time prompt comes from something the
 * driver did.
 */
export function UrgentlyInspectionSheet({
  callId,
  kind,
  open,
  onOpenChange,
}: {
  callId: string;
  kind: UrgentlyInspectionKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = `urgently-inspection-${kind}`;
  const title = URGENTLY_INSPECTION_TITLES[kind];
  const submit = useSubmitInspectionReport();
  const here = useCurrentPosition();
  const form = useZodForm(urgentlyInspectionSchema, { defaultValues: EMPTY });
  const photos = form.watch('photos');
  const previews = usePreviews(photos);
  const [documentType, setDocumentType] =
    React.useState<UrgentlyDocumentType | null>(null);
  const [removing, setRemoving] = React.useState<string | null>(null);

  const { locate, position } = here;
  React.useEffect(() => {
    if (open && !position) locate();
  }, [open, position, locate]);

  const close = (next: boolean) => {
    if (!next) form.reset(EMPTY);
    onOpenChange(next);
  };

  // The type is the one chosen before the picker opened: the add sheet
  // closes as the picker opens, which clears the screen's.
  const add =
    (type: UrgentlyDocumentType) =>
    ({ files }: PickedMedia) => {
      const capturedAt = new Date().toISOString();
      form.setValue('photos', [
        ...form.getValues('photos'),
        ...files.map((file, index) => ({
          id: `${capturedAt}-${index}`,
          file,
          documentType: type,
          capturedAt,
        })),
      ]);
    };

  const send = async (values: UrgentlyInspectionValues) => {
    try {
      const dataUrls = await Promise.all(
        values.photos.map((photo) => toPhotoDataUrl(photo.file))
      );
      if (!fitsOneRequest(dataUrls)) {
        toast.error(
          'Too many photos to send at once. Remove a few and send again.'
        );
        return;
      }
      await submit.mutateAsync({
        dispatchId: callId,
        data: toInspectionReport(kind, values, dataUrls, here.position),
      });
    } catch (error) {
      toast.error(
        (error as Error | null)?.message ||
          'The inspection could not be sent to Urgently.'
      );
      return;
    }
    toast.success('Inspection sent to Urgently.');
    close(false);
  };

  const label = (type: UrgentlyDocumentType) =>
    URGENTLY_DOCUMENT_TYPES.find((option) => option.value === type)?.label;

  return (
    <BottomSheet open={open} onOpenChange={close}>
      <BottomSheetContent
        title={title}
        size="full"
        footer={
          <>
            <BottomSheetClose asChild>
              <Button
                variant="secondary"
                size="xl"
                pill
                disabled={submit.isPending}
              >
                Cancel
              </Button>
            </BottomSheetClose>
            <Button
              type="submit"
              form={formId}
              size="xl"
              pill
              loading={submit.isPending}
            >
              Send to Urgently
            </Button>
          </>
        }
      >
        <Form form={form} id={formId} gap={6} onSubmit={send}>
          <FormField form={form} name="vehicleDamaged" label="Damage">
            {(field) => (
              <Switch
                label="The vehicle is damaged"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          </FormField>
          <FormField form={form} name="notes" label="Notes">
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                placeholder="Scratches, dents, anything Urgently should know"
              />
            )}
          </FormField>
          <FormField form={form} name="photos" label="Photos">
            {() => (
              <Stack gap={3}>
                {photos.length ? (
                  <PhotoStrip
                    size="sm"
                    photos={photos.map((photo, index) => ({
                      id: photo.id,
                      src: previews[index],
                    }))}
                    photoLabel={(index) =>
                      label(photos[index].documentType) ?? 'Photo'
                    }
                    onOpen={setRemoving}
                    aria-label="Photos"
                  />
                ) : null}
                {/* What it shows is asked first; the choice opens the camera
                    or library. */}
                <OptionSheet
                  title="What does the photo show?"
                  options={[...URGENTLY_DOCUMENT_TYPES]}
                  onValueChange={(value) =>
                    setDocumentType(value as UrgentlyDocumentType)
                  }
                  trigger={
                    <AddTile width="block">
                      {photos.length ? 'Add another photo' : 'Add a photo'}
                    </AddTile>
                  }
                />
              </Stack>
            )}
          </FormField>
        </Form>
        <AddPhotoSheet
          open={documentType !== null}
          onOpenChange={(next) => {
            if (!next) setDocumentType(null);
          }}
          onTakePhoto={() => {
            if (documentType) void capturePhoto().then(add(documentType));
          }}
          onChooseFromLibrary={() => {
            if (documentType)
              void pickMedia({ allowVideo: false }).then(add(documentType));
          }}
        />
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(next) => {
            if (!next) setRemoving(null);
          }}
          title="Remove this photo?"
          description="It has not been sent yet, so it only leaves this inspection."
          confirmLabel="Remove photo"
          destructive
          onConfirm={() => {
            form.setValue(
              'photos',
              photos.filter((photo) => photo.id !== removing)
            );
            setRemoving(null);
          }}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}
